import { formatTime, fraction, resolvePlay, timeAt } from './eg-audio-state.js';

/**
 * Episode player — DOM wiring only. All logic lives in eg-audio-state.js (the
 * tested module).
 *
 * ONE <audio> ELEMENT FOR THE WHOLE PAGE, created here with preload="none", so
 * the page fetches no audio at all until someone presses play — and then only
 * the window the browser streams around the playhead (the show's host answers
 * byte-range requests; verified 2026-09-03). Every play control on the page
 * points the same element at its own MP3, which is what makes "only one
 * episode plays at a time" true without any bookkeeping.
 *
 * PROGRESSIVE. The row buttons are <a href="<mp3>"> in the Liquid: with no
 * JS (or before this module runs) a press opens the file in the browser,
 * which streams it too. This module intercepts the click and keeps the
 * listener on the page instead.
 *
 * HEADS. A head (snippets/eg-audio-head.liquid) is a set of controls: title,
 * play/pause, a seek slider and the two times. There can be several on a page
 * and they are VIEWS of the one player, never players of their own:
 *   - a FLOATING head (the archive card's) shows whatever episode is loaded,
 *     and is an empty state until the first press;
 *   - a FIXED head (the "start with this episode" card's) is bound to one
 *     episode by data-eg-audio-src: its button plays that episode, its
 *     progress moves only while that episode is loaded, and it rests at
 *     0:00 / its duration otherwise.
 * Because every control routes through one request(), a row, a floating
 * head and a fixed head can never disagree about what is playing.
 *
 * This module tag can appear more than once on a page (every section that
 * renders a head adds one); the browser runs a module once per URL.
 */

const SEEK_STEPS = 1000;

const headEls = [...document.querySelectorAll('[data-eg-audio-head]')];
if (headEls.length) init(headEls);

function init(heads) {
  // Lives in the DOM, inside the first head, rather than as a detached
  // element: a media element with no parent is fair game for garbage
  // collection between events, and Chrome will quietly stall its load.
  const audio = document.createElement('audio');
  audio.preload = 'none';
  heads[0].appendChild(audio);

  const showName = heads[0].dataset.egAudioShow || '';

  const views = heads.map((el) => {
    const total = el.querySelector('[data-eg-audio-total]');
    return {
      el,
      fixedSrc: el.dataset.egAudioSrc || '',
      fixedTitle: el.dataset.egAudioEpisode || '',
      title: el.querySelector('[data-eg-audio-title]'),
      marquee: el.querySelector('[data-eg-audio-marquee]'),
      toggle: el.querySelector('[data-eg-audio-toggle]'),
      seek: el.querySelector('[data-eg-audio-seek]'),
      elapsed: el.querySelector('[data-eg-audio-elapsed]'),
      total,
      restTotal: total ? total.textContent : '0:00', // a fixed head's printed duration
    };
  });

  let loadedSrc = '';
  let loadedTitle = '';
  let scrubbing = false;

  const rows = () => document.querySelectorAll('[data-eg-audio-play]');
  const isActive = (view) => (view.fixedSrc ? view.fixedSrc === loadedSrc : Boolean(loadedSrc));

  function request(src, episodeTitle) {
    const { action } = resolvePlay({ loadedSrc, paused: audio.paused, ended: audio.ended, requestedSrc: src });
    if (action === 'switch') {
      audio.src = src;
      loadedSrc = src;
      loadedTitle = episodeTitle;
      announce();
    }
    if (action === 'restart') audio.currentTime = 0;
    if (action === 'pause') {
      audio.pause();
    } else {
      // play() rejects when the browser blocks it. The element is still paused
      // then, so re-render rather than leave the optimistic "Pause" face up.
      audio.play().catch(render);
    }
    render();
  }

  function announce() {
    if (!('mediaSession' in navigator) || typeof MediaMetadata === 'undefined') return;
    try {
      navigator.mediaSession.metadata = new MediaMetadata({ title: loadedTitle, artist: showName });
    } catch (_) {
      /* lock-screen metadata is a nicety, never a requirement */
    }
  }

  // THE TITLE SCROLLS WHEN IT DOES NOT FIT (Kyle, 2026-09-03: "like iTunes
  // used to"). Measured here, animated in CSS: when the text overruns its
  // box, the box gets the overrun as --eg-marquee-distance and a duration
  // that keeps the speed constant, and data-eg-marquee="on" starts the
  // keyframes and the edge fades. A title that fits gets neither.
  function fitTitle(view) {
    if (!view.marquee || !view.title) return;
    const overrun = view.title.scrollWidth - view.marquee.clientWidth;
    if (overrun > 1) {
      view.marquee.style.setProperty('--eg-marquee-distance', `${-overrun}px`);
      view.marquee.style.setProperty('--eg-marquee-duration', `${Math.max(6, overrun / 24 + 3)}s`);
      view.marquee.dataset.egMarquee = 'on';
    } else {
      delete view.marquee.dataset.egMarquee;
    }
  }

  // WebKit has no "played" track part, so the CSS paints it from this
  // percentage (Firefox uses ::-moz-range-progress and ignores it).
  function paintSeek(view) {
    view.seek.style.setProperty('--eg-seek-progress', `${(Number(view.seek.value) / SEEK_STEPS) * 100}%`);
  }

  function render() {
    const playing = Boolean(loadedSrc) && !audio.paused;

    for (const button of rows()) {
      const mine = (button.dataset.egAudioSrc || button.getAttribute('href')) === loadedSrc;
      const state = mine ? (playing ? 'playing' : 'paused') : '';
      if (state) button.dataset.egAudioState = state;
      else delete button.dataset.egAudioState;
      // Icon-only buttons: the name says what pressing does, plus the episode.
      const name = button.dataset.egAudioEpisode || '';
      button.setAttribute('aria-label', `${state === 'playing' ? 'Pause' : 'Play'} ${name}`.trim());
    }

    for (const view of views) {
      const active = isActive(view);
      // A floating head takes the loaded episode's title and leaves its empty
      // state for good; a fixed head keeps its own title.
      if (!view.fixedSrc && loadedSrc) {
        view.el.dataset.egAudioState = 'loaded';
        if (view.title.textContent !== loadedTitle) view.title.textContent = loadedTitle;
      }
      const showPause = active && playing;
      view.toggle.dataset.egAudioState = showPause ? 'playing' : 'paused';
      view.toggle.setAttribute('aria-label', `${showPause ? 'Pause' : 'Play'} ${view.fixedTitle}`.trim());
      if (active) {
        if (!scrubbing) {
          view.seek.value = String(Math.round(fraction(audio.currentTime, audio.duration) * SEEK_STEPS));
          view.elapsed.textContent = formatTime(audio.currentTime);
        }
        view.total.textContent = formatTime(audio.duration);
        view.seek.setAttribute('aria-valuetext', `${formatTime(audio.currentTime)} of ${formatTime(audio.duration)}`);
      } else {
        view.seek.value = '0';
        view.elapsed.textContent = formatTime(0);
        view.total.textContent = view.restTotal;
        view.seek.setAttribute('aria-valuetext', `0:00 of ${view.restTotal}`);
      }
      paintSeek(view);
      fitTitle(view);
    }
  }

  // On phones the whole row is the tap target (Kyle, 2026-09-03): a tap
  // anywhere on a row that is not already on its control is forwarded to
  // the control, so the accessible button stays the one real control.
  const phone = window.matchMedia('(max-width: 749px)');

  document.addEventListener('click', (event) => {
    let button = event.target.closest('[data-eg-audio-play]');
    if (!button && phone.matches) {
      const row = event.target.closest('.eg-episode');
      if (row && !event.target.closest('a, button, input')) button = row.querySelector('[data-eg-audio-play]');
    }
    if (!button) return;
    event.preventDefault();
    request(button.dataset.egAudioSrc || button.getAttribute('href'), button.dataset.egAudioEpisode || '');
  });

  // The row controls are links with role="button" (the JS-off fallback needs
  // the href). A link only activates on Enter; a button also activates on
  // Space, so Space is wired here to keep the role honest.
  document.addEventListener('keydown', (event) => {
    if (event.key !== ' ') return;
    const button = event.target.closest('[data-eg-audio-play]');
    if (!button) return;
    event.preventDefault();
    button.click();
  });

  for (const view of views) {
    view.toggle.addEventListener('click', () => {
      if (view.fixedSrc) request(view.fixedSrc, view.fixedTitle);
      else if (loadedSrc) request(loadedSrc, loadedTitle);
    });

    view.seek.addEventListener('input', () => {
      if (!isActive(view)) return;
      scrubbing = true;
      view.elapsed.textContent = formatTime(timeAt(Number(view.seek.value) / SEEK_STEPS, audio.duration));
      paintSeek(view);
    });
    view.seek.addEventListener('change', () => {
      if (isActive(view)) audio.currentTime = timeAt(Number(view.seek.value) / SEEK_STEPS, audio.duration);
      scrubbing = false;
      render();
    });
  }

  window.addEventListener('resize', () => views.forEach(fitTitle));

  for (const type of ['play', 'pause', 'ended', 'timeupdate', 'loadedmetadata', 'durationchange']) {
    audio.addEventListener(type, render);
  }
}
